<template>
  <div>
    <SettingGroup title="エマジェンシー・キー割当て">
      <SettingItem label="サイド 2 キーへ割当て">
        <ToggleSwitch
          v-model="form.toggle.emergency.aux"
          :disable="false"
          @input="handleUpdateEmergencyAux"
        />
      </SettingItem>
      <SettingItem label="REM キーへ割当て">
        <ToggleSwitch
          v-model="form.toggle.emergency.rem"
          :disable="false"
          @input="handleUpdateEmergencyRem"
        />
      </SettingItem>
      <SettingItem
        v-if="isBluetoothDeviceIntegrationEnabled"
        label="Bluetooth機器のボタンへ割当て"
      >
        <ToggleSwitch
          v-model="form.toggle.emergency.ble"
          :disable="false"
          @input="handleUpdateEmergencyBle"
        />
        <SelectBox
          :disabled="!form.toggle.emergency.ble"
          :options="selectboxOptions.bleButton"
          :defaultValue="String(form.selectbox.emergency.bleButton)"
          @update="handleSelectEmergencyBleButton"
        />
      </SettingItem>
    </SettingGroup>
  </div>
</template>

<script>
import SelectBox from '@/components/pc/radio/ipradio/atoms/SelectBox.vue'
import ToggleSwitch from '@/components/pc/radio/ipradio/atoms/ToggleSwitch.vue'
import { FEATURE_JKC_BLUETOOTH_DEVICE_INTEGRATION } from '@/components/feature_toggles/feature_toggle'
import SettingGroup from '@/components/pc/radio/ipradio/organisms/layout/SettingGroup.vue'
import SettingItem from '@/components/pc/radio/ipradio/organisms/layout/SettingItem.vue'
import { BC, BC_TRANS_JP } from '@/components/pc/radio/ipradio/config/BCconstants'
import { useRadioSettingsStore } from '@/components/pc/radio/ipradio/stores/bcRadioSettings'
import { mapStores } from 'pinia'

const BLE_BUTTON_LABELS = BC_TRANS_JP.KEY_ASSIGNMENT.GENERAL.EMERGENCY.BLE_BUTTON
const BLE_BUTTON_OPTIONS = Object.freeze([
  { label: BLE_BUTTON_LABELS.BUTTON2, value: 'Button2' },
  { label: BLE_BUTTON_LABELS.BUTTON3, value: 'Button3' },
  { label: BLE_BUTTON_LABELS.BUTTON4, value: 'Button4' },
  { label: BLE_BUTTON_LABELS.BUTTON5, value: 'Button5' },
])

export default {
  name: 'EmergencyKeyAssignmentVue2',
  components: { SelectBox, ToggleSwitch, SettingGroup, SettingItem },
  data () {
    return {
      isBluetoothDeviceIntegrationEnabled: FEATURE_JKC_BLUETOOTH_DEVICE_INTEGRATION,
      selectboxOptions: {
        bleButton: BLE_BUTTON_OPTIONS,
      },
      form: {
        selectbox: {
          emergency: {
            bleButton: BLE_BUTTON_OPTIONS[0].value,
          },
        },
        toggle: {
          emergency: { aux: false, rem: false, ble: false },
        },
      },
      // ストアの一時設定を保持
      setting: null,
    }
  },
  computed: {
    ...mapStores(useRadioSettingsStore),
  },
  created () {
    // ストアから一時設定を取得
    this.setting = this.radioSettingsStore.getTempSetting()

    // 設定 → フォームへ反映
    this.transSetting2Form()

    // フォーム変更を監視して 設定 ← フォーム & 保存
    this.$watch('form', () => {
      this.transForm2Setting()
      this.radioSettingsStore.saveTempSetting(this.setting)
    }, { deep: true })
  },
  methods: {
    // ===== 設定 ← フォーム =====
    transForm2Setting () {
      const data = this.setting?.KeyAssignment?.General
      if (!data || !data.Emergency) return

      data.Emergency.AUX = this.form.toggle.emergency.aux
        ? BC.COMMON.TOGGLE.ENABLE
        : BC.COMMON.TOGGLE.DISABLE

      data.Emergency.REM = this.form.toggle.emergency.rem
        ? BC.COMMON.TOGGLE.ENABLE
        : BC.COMMON.TOGGLE.DISABLE

      if (!this.isBluetoothDeviceIntegrationEnabled) return

      data.Emergency.BLE = this.form.toggle.emergency.ble
        ? BC.COMMON.TOGGLE.ENABLE
        : BC.COMMON.TOGGLE.DISABLE

      data.Emergency.BLEButton = this.form.selectbox.emergency.bleButton
    },

    // ===== 設定 → フォーム =====
    transSetting2Form () {
      const data = this.setting?.KeyAssignment?.General
      if (!data || !data.Emergency) return

      this.form.toggle.emergency.aux = data.Emergency.AUX === BC.COMMON.TOGGLE.ENABLE
      this.form.toggle.emergency.rem = data.Emergency.REM === BC.COMMON.TOGGLE.ENABLE

      if (!this.isBluetoothDeviceIntegrationEnabled) return

      this.form.toggle.emergency.ble = data.Emergency.BLE === BC.COMMON.TOGGLE.ENABLE
      this.form.selectbox.emergency.bleButton =
        data.Emergency.BLEButton || this.selectboxOptions.bleButton[0].value
    },

    // サイド2キー側の副作用
    handleUpdateEmergencyAux (value) {
      const data = this.setting?.KeyAssignment?.SideKey
      if (!data || !data.Side2) return

      if (value) {
        // 有効時：Side2 の Hold を Emergency に固定し、押下/離しは None
        data.Side2.HoldFunction = 'Emergency'
        data.Side2['Function(Press)'] = 'None'
        data.Side2['Function(Release)'] = 'None'
      } else {
        // 無効時：すべて None
        data.Side2.HoldFunction = 'None'
        data.Side2['Function(Press)'] = 'None'
        data.Side2['Function(Release)'] = 'None'
      }
    },

    // REMキー側の副作用
    handleUpdateEmergencyRem (value) {
      const data = this.setting?.KeyAssignment?.MicKey
      if (!data || !data.REM) return

      if (value) {
        // 有効時：REM の Hold を Emergency に固定し、押下/離しは None
        data.REM.HoldFunction = 'Emergency'
        data.REM['Function(Press)'] = 'None'
        data.REM['Function(Release)'] = 'None'
      } else {
        // 無効時：すべて None
        data.REM.HoldFunction = 'None'
        data.REM['Function(Press)'] = 'None'
        data.REM['Function(Release)'] = 'None'
      }
    },

    handleUpdateEmergencyBle (value) {
      const button = this.form.selectbox.emergency.bleButton
      if (!button) return

      if (value) {
        this.applyEmergencyToBleButton(button)
      } else {
        this.clearEmergencyFromBleButton(button)
      }
    },

    handleSelectEmergencyBleButton (value) {
      const previousButton = this.form.selectbox.emergency.bleButton
      this.form.selectbox.emergency.bleButton = value

      if (!this.form.toggle.emergency.ble || previousButton === value) return

      this.clearEmergencyFromBleButton(previousButton)
      this.applyEmergencyToBleButton(value)
    },

    applyEmergencyToBleButton (button) {
      const data = this.setting?.KeyAssignment?.BLEKey
      if (!data || !data[button]) return

      data[button].HoldFunction = 'Emergency'
      data[button]['Function(Press)'] = 'None'
      data[button]['Function(Release)'] = 'None'
    },

    clearEmergencyFromBleButton (button) {
      const data = this.setting?.KeyAssignment?.BLEKey
      if (!data || !data[button]) return

      data[button].HoldFunction = 'None'
      data[button]['Function(Press)'] = 'None'
      data[button]['Function(Release)'] = 'None'
    },
  },
}
</script>
